<template>
	<v-container class="volunteer-report-form">
		<v-layout row>
			<v-btn @click="openProjectView" v-if="!roles.isVolunteer()">
				Podgląd projektu
			</v-btn>
			<v-spacer></v-spacer>
			<v-btn @click="print" class="mr-3 no-print">Wydrukuj</v-btn>
		</v-layout>

		<v-card-title class="mt-3 mb-3">
			<div>
				<h2 class="mb-3">Raport wolontariusza</h2>
				<div class="volunteer-report-form-card header-card-font-size">
					<div>
						<strong class="mr-3">Numer projektu:</strong> {{viewData.projectCode}}
					</div>
					<div>
						<strong class="mr-3">Nazwa projektu:</strong> {{viewData.projectTitle}}
					</div>
					<div>
						<strong class="mr-3">Suma kontrolna:</strong> {{viewData.controlSum}}
					</div>
					<div>
						<strong class="mr-3">Nazwa oferenta:</strong> {{viewData.bidderName}}
					</div>
					<div v-if="viewData.mutualBidders.length">
						<strong class="mr-3">{{ viewData.mutualBidders.length === 1 ? 'Nazwa współoferenta:' : 'Nazwy współoferentów:' }}</strong>
						{{ viewData.mutualBidders.join(', ') }}
					</div>
					<div class="mt-3">
						<strong class="mr-3">Wolontariusz:</strong> {{viewData.author}}
					</div>
					<div style="white-space: pre;">
						<strong class="mr-3">Okres wolontariatu:</strong> {{volunteerPeriod}}
					</div>
					<div>
						<strong class="mr-3">Numer raportu:</strong> {{viewData.reportNumber}}
					</div>
					<div class="mt-3">
						<strong class="mr-3">Status raportu:</strong> {{getStatusText(viewData.reportStatus)}}
					</div>
					<div
						v-if="viewData.finishDate !== null && viewData.reportStatus !== 'Zakończony'">
						<strong class="mr-3">Termin złożenia raportu:</strong> {{viewData.finishDate}}
					</div>
				</div>
			</div>
		</v-card-title>

		<hr class="ma-4">

		<vue-form-generator
			name="generator"
			ref="vfg"
			:model="formGenerator.currentModel"
			:options="formGenerator.formOptions"
			:schema="formGenerator.currentSchema"
			v-if="viewData.showGenerator">
		</vue-form-generator>

		<v-btn @click="saveReport" v-if="!configuration.disabled" color="info" class="no-print">
			<v-icon class="mr-2 white--text">save</v-icon>
			Zapisz raport
		</v-btn>

		<v-btn @click="viewData.sendReportDialog = true" v-if="!configuration.disabled" color="success"
			class="no-print">
			<v-icon class="mr-2 white--text">send</v-icon>
			Wyślij raport
		</v-btn>

		<report-confirmation-dialog :action="send" :dialog-model="viewData.sendReportDialog"
			@close-confirmation="viewData.sendReportDialog = false"
			action-label="Wyślij" content="Zanim wyślesz raport upewnij się, że jest poprawnie wypełniony.
			Po wysłaniu nie będzie już możliwości edycji!" header="Wysyłka raportu" icon="send"/>

		<report-signature class="d-none print-only mt-2 mb-2"></report-signature>
	</v-container>
</template>

<script>
	import {genericFormUtil} from "@/utils/genericFormUtil";
	import ReportConfirmationDialog from "./ReportConfirmationDialog";
	import {VolunteerReport} from "@/api/generated/VolunteerReport";
	import ReportSignature from "./ReportSignature";
	import {Files} from "@/utils/Files";

	export default {
		name: "ReportFormView",
		components: {ReportSignature, ReportConfirmationDialog},

		data () {
			return {
				configuration: {
					form: "",
					disabled: false,
				},

				viewData: {
					projectId: this.$route.params.projectId,
					volunteerReportId: this.$route.params.volunteerReportId,
					author: "",
					projectTitle: "",
					reportNumber: null,
					showGenerator: false,
					sendReportDialog: false,
					projectCode: "",
					controlSum: "",
					bidderName: "",
          mutualBidders: [],
					finishDate: null,
					reportStatus: "",
					volunteeringTerms: []
				},

				formGenerator: {
					currentSchema: {},
					currentModel: {},
					formOptions: {
						validateAfterLoad: false,
						validateAfterChanged: true,
						validateAsync: true,
					},
					currentConditionalHashes: [],
				},
			};
		},

		watch: {
			"formGenerator.currentModel": {
				handler () {
					this.applyGenericForm(this.configuration.form);
				},
				deep: true,
			},
		},

		computed: {
			volunteerPeriod() {
				let label = "";
				this.viewData.volunteeringTerms.forEach((vt, index) => {
					if (index !== 0) label += ",\t";
					label += vt.volunteeringStartDate + " do " + vt.volunteeringEndDate;
				});
				return label;
			},
		},

		created () {
			this.loadData();
		},

		methods: {
			async loadData () {
				await VolunteerReport.getConfiguration(this, this.viewData.volunteerReportId, successResponse => {
					if (successResponse.schema !== null) {
						this.configuration.form = genericFormUtil.decodeSchema(successResponse.schema);
						this.formGenerator.currentSchema = genericFormUtil.getSectionSchemaFromSchema(this.configuration.form, 0, this.configuration.disabled);
					}
				});

				VolunteerReport.getFilledReport(this, this.viewData.volunteerReportId, successResponse => {
					if (successResponse.status === "FINISHED") this.configuration.disabled = true;
					if (Object.keys(this.formGenerator.currentSchema).length !== 0) {
						this.formGenerator.currentModel = successResponse.volunteerFilledReport === null ?
							genericFormUtil.buildModelFromSchema(this.configuration.form).sections[0].fields :
							genericFormUtil.decodeSchema(successResponse.volunteerFilledReport);
						this.applyGenericForm(this.configuration.form);
					}
					this.viewData.author = successResponse.author;
					this.viewData.projectTitle = successResponse.projectTitle;
					this.viewData.reportNumber = successResponse.reportNumber;
					this.viewData.showGenerator = true;
					this.viewData.projectCode = successResponse.projectCode;
					this.viewData.controlSum = successResponse.controlSum;
					this.viewData.bidderName = successResponse.bidderName;
          this.viewData.mutualBidders = successResponse.mutualBidders;
					this.viewData.reportStatus = successResponse.status;
					this.viewData.finishDate = successResponse.finishDate;
					this.viewData.volunteeringTerms = successResponse.volunteeringTerms;
				});
			},

			saveReport () {
				VolunteerReport.putData(this, this.viewData.volunteerReportId, this.prepareFormData(), successResponse => {
					this.$messagesBus.$emit("success", "Pomyślnie zapisano raport", successResponse);
					this.loadData();
				}, errorResponse => {
					this.$messagesBus.$emit("error", "Nie udało się zapisać raportu", errorResponse);
				});
			},

			async send () {
				let validationPromise = await this.$refs.vfg.validate();
				if (validationPromise.length > 0) {
					return;
				}
				VolunteerReport.putFinish(this, this.viewData.volunteerReportId, this.prepareFormData(), successResponse => {
					this.$messagesBus.$emit("success", "Raport został wysłany", successResponse);
					this.$router.push("/app/report");
				}, errorResponse => {
					this.$messagesBus.$emit("error", "Nie udało się wysłać raportu", errorResponse);
				});
			},

			prepareFormData () {
				return {
					schema: genericFormUtil.encodeSchema(this.formGenerator.currentModel),
				};
			},

			applyGenericForm (form) {
				let schema = genericFormUtil.getSectionSchemaFromSchema(form, 0, this.configuration.disabled);
				if (genericFormUtil.sectionSchemaContainsConditionalField(schema)) {
					let schemaValues = this.formGenerator.currentModel;
					this.formGenerator.currentSchema = genericFormUtil.filterSchemaByConditionalFieldsValue(schema, schemaValues);
					let indexes = genericFormUtil.calculateIndexes(schema);
					this.formGenerator.currentConditionalHashes = genericFormUtil.getConditionalValueHashes(schema, indexes);
				} else {
					this.formGenerator.currentSchema = schema;
				}
			},

			getStatusText(status) {
				switch (status) {
					case "STARTED":
						return "Rozpoczęty";
					case "REVISION":
						return "Oddany do poprawki";
					case "FINISHED":
						return "Zakończony";
					case "NEW":
						return "Nierozpoczęty";
				}
			},

      openProjectView () {
        this.$router.openInNewTab('/view/project/' + this.viewData.projectId);
      },

			async print () {
				await Files.methods.downloadFileAsBlob(
					this,
					Files.methods.getReportPrintUrl(
						this.viewData.projectId,
						"volunteer",
						this.viewData.volunteerReportId),
					"raport_wolontariusza.pdf"
				);
			},
		},
	};
</script>

<style scoped>
	.header-card-font-size {
		font-size: 14px;
	}

	@media print {
		.header-card-font-size {
			font-size: 13px;
		}
	}
</style>
